/* eslint-disable no-console */
import localforage from "localforage";

// Configure localforage
// Configure localforage only on client side
if (typeof window !== "undefined") {
	localforage.config({
		name: "settlin",
		version: 1.0,
		storeName: "settlin_storage",
		description: "Settlin application storage using IndexedDB",
	});
}

/**
 * Sanitize data for IndexedDB storage by doing a JSON round-trip
 * This removes circular references, functions, and other non-serializable data
 */
function sanitizeForStorage<T>(data: T): T {
	try {
		// JSON round-trip to ensure data is serializable
		const serialized = JSON.stringify(data);
		return JSON.parse(serialized) as T;
	} catch (error) {
		console.error("Error sanitizing data for storage:", error);
		throw error;
	}
}

/**
 * Storage utility that provides a consistent interface for storage operations
 * Uses IndexedDB via localforage for better performance and larger storage capacity
 */
export const storage = {
	/**
	 * Get an item from storage
	 */
	async getItem<T = string>(key: string): Promise<T | null> {
		if (typeof window === "undefined") return null;
		try {
			return await localforage.getItem<T>(key);
		} catch (error) {
			console.error(`Error getting item ${key} from storage:`, error);
			return null;
		}
	},

	/**
	 * Set an item in storage with automatic sanitization
	 */
	async setItem<T = string>(key: string, value: T): Promise<void> {
		if (typeof window === "undefined") return;
		try {
			// Sanitize the data to ensure it's serializable
			const sanitizedValue = sanitizeForStorage(value);
			await localforage.setItem(key, sanitizedValue);
		} catch (error) {
			console.error(`Error setting item ${key} in storage:`, error);
			throw error;
		}
	},

	/**
	 * Remove an item from storage
	 */
	async removeItem(key: string): Promise<void> {
		if (typeof window === "undefined") return;
		try {
			await localforage.removeItem(key);
		} catch (error) {
			console.error(`Error removing item ${key} from storage:`, error);
		}
	},

	/**
	 * Clear all items from storage
	 */
	async clear(): Promise<void> {
		if (typeof window === "undefined") return;
		try {
			await localforage.clear();
		} catch (error) {
			console.error("Error clearing storage:", error);
		}
	},

	/**
	 * Get all keys from storage
	 */
	async keys(): Promise<string[]> {
		if (typeof window === "undefined") return [];
		try {
			return await localforage.keys();
		} catch (error) {
			console.error("Error getting storage keys:", error);
			return [];
		}
	},

	/**
	 * Get the number of items in storage
	 */
	async length(): Promise<number> {
		if (typeof window === "undefined") return 0;
		try {
			return await localforage.length();
		} catch (error) {
			console.error("Error getting storage length:", error);
			return 0;
		}
	},
};

/**
 * Synchronous storage fallback for cases where async storage cannot be used
 * Falls back to localStorage for compatibility
 */
export const syncStorage = {
	getItem(key: string): string | null {
		if (typeof window === "undefined" || !window.localStorage) return null;
		try {
			return window.localStorage.getItem(key);
		} catch (error) {
			console.error(`Error getting item ${key} from localStorage:`, error);
			return null;
		}
	},

	setItem(key: string, value: string): void {
		if (typeof window === "undefined" || !window.localStorage) return;
		try {
			window.localStorage.setItem(key, value);
		} catch (error) {
			console.error(`Error setting item ${key} in localStorage:`, error);
		}
	},

	removeItem(key: string): void {
		if (typeof window === "undefined" || !window.localStorage) return;
		try {
			window.localStorage.removeItem(key);
		} catch (error) {
			console.error(`Error removing item ${key} from localStorage:`, error);
		}
	},
};
